/*!
 * cueParamBinding.js — Dynamic Parameter Binding for Animation Cues
 * Part of oscillaScore control plane architecture
 * © 2025 Rob Canning — GPLv3
 *
 * Provides a centralized, reusable interface for animation cues (rotate, o2p,
 * scale, color, etc.) to bind parameters to signal sources.
 *
 * Usage:
 *   import { setupSpeedBinding, setupParamBinding } from './cueParamBinding.js';
 *
 *   // In your cue handler:
 *   const speedBinding = setupSpeedBinding(cfg, durValue, {
 *     baseDur: 2,
 *     minDur: 0.5,
 *     maxDur: 10
 *   });
 *
 *   // Later, in your animation loop:
 *   const adjustedDur = baseDur / cfg.speedMultiplier;
 *
 *   // Cleanup when animation stops:
 *   speedBinding.unbind();
 */

// Per-update logging is behind window.OSCILLA_BINDING_DEBUG: a bound animation
// writes on every frame, so it would bury everything else in the console.

import { bindParam, isSignalRef } from '../control/paramBinding.js';
import { invertCurve } from '../control/curves.js';
import { serverNow } from '../system/serverClock.js';
import { settleSpeedOf } from './cueAnimLoop.js';

// ============================================================================
// Fader Default Registry
// ============================================================================

/**
 * Registry of default positions for faders.
 * Key: "source.channel", Value: { min, max, default, t }
 * t is the normalized 0-1 position for the fader
 */
const faderDefaults = new Map();

/**
 * Registry of fader update callbacks.
 * Key: fader uid, Value: (t) => void
 */
const faderUpdateCallbacks = new Map();


/**
 * Register a fader default so it can initialize at the right position.
 * @param {string} source - Fader uid
 * @param {string} channel - Channel name (usually 't')
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {number} defaultVal - Default value
 * @param {string} curve - Curve type for mapping (default: 'lin')
 */
export function registerFaderDefault(source, channel, min, max, defaultVal, curve = 'lin') {
    const key = `${source}.${channel}`;
    // Calculate normalized output position
    const normalizedOutput = (defaultVal - min) / (max - min);
    // Invert the curve to find the fader position that produces this output
    const t = invertCurve(normalizedOutput, curve);
    const clampedT = Math.max(0, Math.min(1, t));
    faderDefaults.set(key, { min, max, default: defaultVal, t: clampedT, curve });

    // If fader already exists, update its position
    const callback = faderUpdateCallbacks.get(source);
    if (callback) {
        if (window.OSCILLA_BINDING_DEBUG) console.log(`[cueParamBinding] Updating existing fader ${source} to default position`);
        callback(clampedT);
    }
}

/**
 * Register a callback to update a fader's position.
 * Called by o2p.js when creating touch faders.
 * @param {string} uid - Fader uid
 * @param {Function} callback - (t: number) => void
 */
export function registerFaderUpdateCallback(uid, callback) {
    faderUpdateCallbacks.set(uid, callback);

    // Check if a default is already registered for this fader
    const defaultEntry = faderDefaults.get(`${uid}.t`);
    if (defaultEntry) {
        callback(defaultEntry.t);
    }
}

/**
 * Unregister a fader update callback.
 * @param {string} uid - Fader uid
 */
export function unregisterFaderUpdateCallback(uid) {
    faderUpdateCallbacks.delete(uid);
}

/**
 * Get the default normalized position (0-1) for a fader.
 * @param {string} source - Fader uid
 * @param {string} channel - Channel name (usually 't')
 * @returns {number|null} Normalized position 0-1, or null if no default registered
 */
export function getFaderDefaultPosition(source, channel = 't') {
    const key = `${source}.${channel}`;
    const entry = faderDefaults.get(key);
    return entry?.t ?? null;
}

/**
 * Get all registered fader defaults.
 * @returns {Map} The fader defaults registry
 */
export function getFaderDefaults() {
    return faderDefaults;
}

// ============================================================================
// Speed/Duration Binding
// ============================================================================

/**
 * Set up dynamic speed control via duration binding.
 *
 * When dur is bound to a signal, speedMultiplier is updated inversely:
 * - Lower dur value = higher speedMultiplier (faster animation)
 * - Higher dur value = lower speedMultiplier (slower animation)
 *
 * @param {Object} cfg - Cue configuration object
 * @param {any} durValue - Duration value (number or signalRef object)
 * @param {Object} options - Binding options
 * @param {number} options.baseDur - Base duration (default from durValue or 1)
 * @param {number} options.minDur - Minimum duration (default: 0.1)
 * @param {number} options.maxDur - Maximum duration (default: 120)
 * @returns {{ baseDur: number, unbind: Function, isBound: boolean }}
 */
export function setupSpeedBinding(cfg, durValue, options = {}) {
  // Initialize speedMultiplier if not present
  cfg.speedMultiplier = cfg.speedMultiplier ?? 1;

  // If not a signal reference, just return the static value
  if (!isSignalRef(durValue)) {
    const staticDur = Number(durValue) || options.baseDur || 1;
    return {
      baseDur: staticDur,
      unbind: () => {},
      isBound: false
    };
  }

  // Extract range from signal ref or use options
  const minDur = durValue.range?.[0] ?? options.minDur ?? 0.1;
  const maxDur = durValue.range?.[1] ?? options.maxDur ?? 120;
  // Curve from signal ref or default to linear
  const durCurve = durValue.curve || 'lin';

  // A cue that gives no speed limits gets the duration it was told:
  // dur:follow(x, 8, 0.3) is 8 s at one end and 0.3 s at the other, as the
  // docs write it. (It was a speed of x0.25..x4 of the first number -- 32 s
  // to 2 s there -- whatever the range said.) o2p passes its own limits.
  const exactDur = options.minSpeed === undefined && options.maxSpeed === undefined;
  const builtDur = Number(options.baseDur) > 0 ? Number(options.baseDur) : (durValue.range?.[0] ?? 1);
  const minSpeed = options.minSpeed ?? 0.25;
  const maxSpeed = options.maxSpeed ?? 4;
  const bipolar  = options.bipolar ?? false;

  // Bipolar faders default to centre (t=0.5 = rest) unless the signal ref
  // names a default (…-min-max-default): then they start there, e.g. just right
  // of centre for a slow forward motion at load.
  // Unipolar faders only register a default when one is explicitly provided,
  // so presets/localStorage can control initial state otherwise.
  const hasExplicitDefault = bipolar || durValue.default !== undefined || options.baseDur !== undefined;
  const baseDur = bipolar
    ? (durValue.default ?? (minDur + maxDur) / 2)   // midpoint → t=0.5 → speedMultiplier=0
    : (durValue.default ?? options.baseDur ?? minDur);

  if (hasExplicitDefault) {
    registerFaderDefault(durValue.source, durValue.channel, minDur, maxDur, baseDur, durCurve);
  } else {
    if (window.OSCILLA_BINDING_DEBUG) console.log(`[cueParamBinding] No default for ${durValue.source}.${durValue.channel} - letting presets/localStorage control initial state`);
  }

  // Set up the binding
  // Default: Left = slow, Right = fast (unipolar)
  // With bipolar: Left = negative (reverse), Centre = stopped, Right = positive (forward)

  // Owned by this cfg's dur slot: re-running the cue replaces the binding
  // instead of stacking another one that still writes to the old cfg.
  const durOwner = `${options.kind || cfg.kind || 'anim'}:${cfg.uid || 'anon'}:dur`;

  const binding = bindParam(durValue, (mappedDur, rawValue) => {
    const before = cfg.speedMultiplier;
    settleSpeedOf(cfg);       // the motion so far counts at the old speed
    if (bipolar) {
      // Bipolar mode: use RAW fader position (0-1) directly, ignoring curve
      // center (0.5) = stopped, left = reverse, right = forward
      // rawValue=0 → -maxSpeed, rawValue=0.5 → 0, rawValue=1 → +maxSpeed
      cfg.speedMultiplier = (rawValue - 0.5) * 2 * maxSpeed;
      if (window.OSCILLA_BINDING_DEBUG) console.log(`[cueParamBinding] speedMultiplier = ${cfg.speedMultiplier.toFixed(2)} (bipolar, raw=${rawValue.toFixed(2)})`);
    } else {
      // Unipolar mode: use mapped duration value
      const t = (mappedDur - minDur) / (maxDur - minDur); // 0-1 normalized
      cfg.speedMultiplier = exactDur
        ? builtDur / Math.max(0.02, mappedDur)      // runs at exactly mappedDur
        : minSpeed + t * (maxSpeed - minSpeed);
      if (window.OSCILLA_BINDING_DEBUG) console.log(`[cueParamBinding] speedMultiplier = ${cfg.speedMultiplier.toFixed(2)} (t=${t.toFixed(2)})`);
    }
    // when the speed changed, on the shared clock: animSync ignores a leader
    // position measured before it
    if (cfg.speedMultiplier !== before) cfg._syncChangedAt = serverNow();
  }, {
    owner: durOwner,
    min: minDur,
    max: maxDur,
    default: baseDur
  });

  return {
    baseDur,
    unbind: binding.unbind,
    isBound: true,
    path: binding.path
  };
}

// ============================================================================
// Generic Parameter Binding
// ============================================================================

/**
 * Set up a generic parameter binding.
 * Updates cfg[paramName] when the signal changes.
 *
 * @param {Object} cfg - Cue configuration object
 * @param {string} paramName - Parameter name to update on cfg
 * @param {any} paramValue - Parameter value (number or signalRef object)
 * @param {Object} options - Binding options
 * @param {number} options.min - Minimum value (default: 0)
 * @param {number} options.max - Maximum value (default: 1)
 * @param {number} options.default - Default value
 * @param {Function} options.transform - Optional transform function (rawValue) => transformedValue
 * @returns {{ value: number, unbind: Function, isBound: boolean }}
 */
export function setupParamBinding(cfg, paramName, paramValue, options = {}) {
  const {
    min = 0,
    max = 1,
    default: defaultValue,
    transform = (v) => v
  } = options;

  // If not a signal reference, just return the static value
  if (!isSignalRef(paramValue)) {
    const staticValue = transform(Number(paramValue) || defaultValue || min);
    cfg[paramName] = staticValue;
    return {
      value: staticValue,
      unbind: () => {},
      isBound: false
    };
  }

  // Extract range from signal ref or use options
  const sigMin = paramValue.range?.[0] ?? min;
  const sigMax = paramValue.range?.[1] ?? max;
  // Curve from signal ref or default to linear
  const sigCurve = paramValue.curve || 'lin';

  // Only register fader default if explicitly provided in signal ref or options
  // This allows presets/localStorage to control initial state when no default specified
  const hasExplicitDefault = paramValue.default !== undefined || defaultValue !== undefined;
  const sigDefault = paramValue.default ?? defaultValue ?? sigMin;

  if (hasExplicitDefault) {
    registerFaderDefault(paramValue.source, paramValue.channel, sigMin, sigMax, sigDefault, sigCurve);
  } else {
    if (window.OSCILLA_BINDING_DEBUG) console.log(`[cueParamBinding] No default for ${paramValue.source}.${paramValue.channel} - letting presets/localStorage control initial state`);
  }

  // Initialize param (use sigDefault but it won't override saved state if no explicit default)
  cfg[paramName] = sigDefault;

  // Set up the binding
  const binding = bindParam(paramValue, (mappedValue) => {
    cfg[paramName] = transform(mappedValue);
  }, {
    min: sigMin,
    max: sigMax,
    default: sigDefault,
    owner: `${cfg.kind || 'anim'}:${cfg.uid || 'anon'}:${paramName}`
  });

  return {
    value: sigDefault,
    unbind: binding.unbind,
    isBound: true,
    path: binding.path
  };
}

// ============================================================================
// Re-export isSignalRef for convenience
// ============================================================================

export { isSignalRef, isOscRef } from '../control/paramBinding.js';

